import React, { useState, useEffect } from 'react';
import { Outlet, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import {
  LayoutDashboard,
  Users,
  FolderOpen,
  FileText,
  Shield,
  Settings,
  LogOut,
  ChevronRight,
  MessageSquare,
  Home,
  History,
  DollarSign,
  BookOpen,
  Menu
} from 'lucide-react';

const menuItems = [
  { id: 'dashboard', label: '仪表盘', icon: LayoutDashboard, path: '/admin' },
  { id: 'users', label: '用户管理', icon: Users, path: '/admin/users' },
  { id: 'posts', label: '帖子管理', icon: FileText, path: '/admin/posts' },
  { id: 'experience', label: '心得管理', icon: BookOpen, path: '/admin/experience' },
  { id: 'categories', label: '版块管理', icon: FolderOpen, path: '/admin/categories' },
  { id: 'sensitive-words', label: '敏感词管理', icon: Shield, path: '/admin/sensitive-words' },
  { id: 'chat-admin', label: '聊天室管理', icon: MessageSquare, path: '/admin/chat' },
  { id: 'operation-logs', label: '操作日志', icon: History, path: '/admin/operation-logs' },
];

const AdminLayout: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isHydrating, setIsHydrating] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const timer = setTimeout(() => {
        setIsHydrating(false);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setIsHydrating(false);
    }
  }, []);

  useEffect(() => {
    if (!isHydrating && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, isHydrating, navigate]);

  if (isLoading || isHydrating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-pink-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">加载中...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !['管理员', '站长', '超级管理员', '吧主', '吧务'].includes(user?.role || '')) {
    return <Navigate to="/login" replace />;
  }

  const getActiveMenu = () => {
    const path = location.pathname;
    if (path === '/admin') return 'dashboard';
    if (path.startsWith('/admin/users')) return 'users';
    if (path.startsWith('/admin/posts')) return 'posts';
    if (path.startsWith('/admin/experience')) return 'experience';
    if (path.startsWith('/admin/categories')) return 'categories';
    if (path.startsWith('/admin/sensitive-words')) return 'sensitive-words';
    if (path.startsWith('/admin/site-config')) return 'site-config';
    if (path.startsWith('/admin/chat')) return 'chat-admin';
    if (path.startsWith('/admin/room-management')) return 'room-management';
    if (path.startsWith('/admin/expansion-orders')) return 'expansion-orders';
    if (path.startsWith('/admin/donation-orders')) return 'donation-orders';
    if (path.startsWith('/admin/pending-orders')) return 'pending-orders';
    if (path.startsWith('/admin/operation-logs')) return 'operation-logs';
    return 'dashboard';
  };

  const activeMenu = getActiveMenu();

  const handleMenuClick = (path: string) => {
    navigate(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* 侧边栏 */}
      <aside className={`bg-white shadow-lg flex flex-col ${sidebarCollapsed ? 'w-16' : 'w-64'} transition-all duration-300`}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200 bg-gradient-to-r from-pink-600 to-pink-700">
          <div className="flex items-center">
            {sidebarCollapsed ? (
              <span className="text-white font-bold text-xl">🌸</span>
            ) : (
              <span className="text-white font-bold text-lg">管理后台</span>
            )}
          </div>
          {/* 折叠按钮 */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>

        {/* 返回前台按钮 */}
        <button
          onClick={() => navigate('/')}
          className="w-full flex items-center px-4 py-3 text-left text-gray-600 hover:bg-gray-50 transition-colors border-b border-gray-200"
        >
          <Home className={`w-5 h-5 ${sidebarCollapsed ? '' : 'mr-3'}`} />
          {!sidebarCollapsed && (
            <span className="text-sm font-medium">返回前台</span>
          )}
        </button>

        {/* 菜单 */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeMenu === item.id;
            return (
              <button
                key={item.id}
                onClick={() => handleMenuClick(item.path)}
                className={`w-full flex items-center px-4 py-3 text-left transition-colors ${
                  isActive
                    ? 'bg-pink-50 text-pink-600 border-r-4 border-pink-600'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Icon className={`w-5 h-5 ${sidebarCollapsed ? '' : 'mr-3'}`} />
                {!sidebarCollapsed && (
                  <span className="text-sm font-medium">{item.label}</span>
                )}
              </button>
            );
          })}

          {/* 站点配置 - 仅站长可见 */}
          {user?.role === '站长' && (
            <button
              onClick={() => handleMenuClick('/admin/site-config')}
              className={`w-full flex items-center px-4 py-3 text-left transition-colors ${
                activeMenu === 'site-config'
                  ? 'bg-pink-50 text-pink-600 border-r-4 border-pink-600'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Settings className={`w-5 h-5 ${sidebarCollapsed ? '' : 'mr-3'}`} />
              {!sidebarCollapsed && (
                <span className="text-sm font-medium">站点配置</span>
              )}
            </button>
          )}
        </nav>

        {/* 用户信息 */}
        <div className="border-t border-gray-200 p-4">
          <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : ''}`}>
            <img
              src={user?.avatar || `https://ui-avatars.com/api/?name=${user?.nickname || user?.username || 'User'}&background=3b82f6&color=fff`}
              alt={user?.nickname || user?.username}
              className="w-10 h-10 rounded-full"
            />
            {!sidebarCollapsed && (
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{user?.nickname}</p>
                <p className="text-xs text-gray-500 truncate">{user?.role}</p>
              </div>
            )}
          </div>
          {!sidebarCollapsed && (
            <button
              onClick={handleLogout}
              className="mt-3 w-full flex items-center justify-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4 mr-2" />
              退出登录
            </button>
          )}
        </div>
      </aside>

      {/* 主内容区 */}
      <main className="flex-1 overflow-auto">
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;